import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AvailableModelSelector } from '@modules/ai-settings';
import { AgentApiClient } from '@modules/content-agent/services/AgentApiClient';
import { useBackendTask } from '@modules/content-agent/hooks';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import { MediaUploadField, SDialog, StyledTextField } from '@modules/ui-components';
import type { AgentGenerateThemeV2FromImageResponse } from '@modules/shared-types';
import { createThemeV2DraftFromProjection } from '@modules/themes/projection';
import type { ThemeV2ProjectionPatch } from '@modules/themes/projection';
import {
  type CreateThemeV2Input,
  type ThemeV2,
} from '@modules/themes/types/themesV2.types';
import { ThemeV2PreviewCarousel } from '@modules/themes/components/ThemeV2PreviewCarousel';
import { createEditableThemeV2FromDraft } from '@modules/themes/utils';

export interface GenerateThemeFromImageDialogProps {
  open: boolean;
  projectId: string;
  baseTheme?: ThemeV2 | null;
  title?: string;
  createActionLabel?: string;
  liveAnalysis?: boolean;
  persistAnalysis?: boolean;
  onClose: () => void;
  onCreateTheme: (theme: CreateThemeV2Input) => Promise<unknown>;
}

interface SourceImageInput {
  id: string;
  value: string | File | null;
  uploading: boolean;
}

interface PendingGenerationContext {
  themeName: string;
  sourceImageUrls: string[];
  model: string;
}

const MAX_SOURCE_IMAGES = 8;

function createSourceImageInput(index: number, keySuffix = ''): SourceImageInput {
  return {
    id: keySuffix ? `source_image_${index + 1}_${keySuffix}` : `source_image_${index + 1}`,
    value: null,
    uploading: false,
  };
}

function sanitizeUploadFilename(filename: string): string {
  const withoutExtension = filename.replace(/\.[^.]+$/, '');
  const safe = withoutExtension
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9_-]/g, '')
    .toLowerCase();
  return safe || 'theme-reference';
}

function getSourceImageUrl(source: SourceImageInput): string | null {
  return typeof source.value === 'string' && source.value.trim() ? source.value.trim() : null;
}

export const GenerateThemeFromImageDialog: React.FC<GenerateThemeFromImageDialogProps> = ({
  open,
  projectId,
  baseTheme,
  title = 'Generate Theme From Image',
  createActionLabel = 'Create Theme',
  liveAnalysis = true,
  persistAnalysis,
  onClose,
  onCreateTheme,
}) => {
  const [themeName, setThemeName] = React.useState('Generated Image Theme');
  const [sourceImages, setSourceImages] = React.useState<SourceImageInput[]>([createSourceImageInput(0)]);
  const [model, setModel] = React.useState('gpt-5.6-terra');
  const [draftTheme, setDraftTheme] = React.useState<CreateThemeV2Input | null>(null);
  const [warnings, setWarnings] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [generating, setGenerating] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [activeTaskId, setActiveTaskId] = React.useState<string | null>(null);
  const [pendingGeneration, setPendingGeneration] = React.useState<PendingGenerationContext | null>(null);
  const { task: generationTask, error: taskError } = useBackendTask(
    open && activeTaskId ? projectId : null,
    open ? activeTaskId : null
  );

  React.useEffect(() => {
    if (!open) {
      setThemeName('Generated Image Theme');
      setSourceImages([createSourceImageInput(0)]);
      setModel('gpt-5.6-terra');
      setDraftTheme(null);
      setWarnings([]);
      setError(null);
      setGenerating(false);
      setSaving(false);
      setActiveTaskId(null);
      setPendingGeneration(null);
    }
  }, [open]);

  const sourceImageUrls = React.useMemo(
    () => sourceImages.map(getSourceImageUrl).filter((value): value is string => Boolean(value)),
    [sourceImages]
  );
  const hasPendingUploads = sourceImages.some((source) => source.uploading || source.value instanceof File);
  const canGenerate = Boolean(
    projectId &&
    themeName.trim() &&
    sourceImageUrls.length > 0 &&
    !hasPendingUploads &&
    !generating &&
    !saving
  );

  const uploadService = React.useMemo(() => new StorageService(), []);

  const updateSourceImage = React.useCallback((index: number, patch: Partial<SourceImageInput>) => {
    setSourceImages((current) =>
      current.map((source, sourceIndex) => (
        sourceIndex === index ? { ...source, ...patch } : source
      ))
    );
  }, []);

  const handleSourceImageChange = React.useCallback(async (index: number, value: string | File | null) => {
    setError(null);
    setDraftTheme(null);

    if (!(value instanceof File)) {
      updateSourceImage(index, { value, uploading: false });
      return;
    }

    updateSourceImage(index, { value, uploading: true });
    try {
      const uploadPath = [
        'projects',
        projectId,
        'theme-v2',
        'source-images',
        `${Date.now()}_${sanitizeUploadFilename(value.name)}`,
      ].join('/');
      const result = await uploadService.uploadImage(uploadPath, value);
      updateSourceImage(index, {
        value: result.assetUrl.origin || result.assetUrl.normal || result.assetUrl.small,
        uploading: false,
      });
    } catch (err) {
      updateSourceImage(index, { value: null, uploading: false });
      setError(err instanceof Error ? err.message : 'Failed to upload image');
    }
  }, [projectId, updateSourceImage, uploadService]);

  const handleAddSourceImage = React.useCallback(() => {
    setSourceImages((current) => {
      if (current.length >= MAX_SOURCE_IMAGES) return current;
      return [...current, createSourceImageInput(current.length, String(Date.now()))];
    });
  }, []);

  const handleRemoveSourceImage = React.useCallback((index: number) => {
    setSourceImages((current) => {
      if (current.length === 1) {
        return [createSourceImageInput(0)];
      }
      return current.filter((_, sourceIndex) => sourceIndex !== index);
    });
    setDraftTheme(null);
  }, []);

  const applyGenerationResponse = React.useCallback((
    response: AgentGenerateThemeV2FromImageResponse,
    context: PendingGenerationContext
  ) => {
    const draft = createThemeV2DraftFromProjection({
      name: context.themeName,
      baseTheme,
      projectionPatch: response.projectionPatch as ThemeV2ProjectionPatch,
      analysis: response.analysis,
      sourceImageUrls: context.sourceImageUrls,
      model: context.model,
      warnings: response.warnings,
    });

    setDraftTheme(draft);
    setWarnings(draft.generatedMetadata?.warnings || response.warnings || []);
    setGenerating(false);
    setActiveTaskId(null);
    setPendingGeneration(null);
  }, [baseTheme]);

  const handleGenerate = React.useCallback(async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError(null);
    setWarnings([]);
    setDraftTheme(null);
    setActiveTaskId(null);

    const context: PendingGenerationContext = {
      themeName: themeName.trim(),
      sourceImageUrls: [...sourceImageUrls],
      model,
    };
    setPendingGeneration(context);

    try {
      const client = new AgentApiClient();
      const response = await client.generateThemeV2FromImage({
        projectId,
        sourceKind: 'user-image',
        model,
        liveAnalysis,
        persist: persistAnalysis,
        images: sourceImageUrls.map((sourceUrl, index) => ({
          id: `source_image_${index + 1}`,
          sourceUrl,
        })),
      });

      if (!response.success) {
        setError(response.error || 'Failed to start Theme V2 generation');
        setGenerating(false);
        setPendingGeneration(null);
        return;
      }

      if (response.projectionPatch) {
        applyGenerationResponse(response, context);
        return;
      }

      if (response.taskId) {
        setActiveTaskId(response.taskId);
        return;
      }

      setError('Theme V2 generation did not return a task or projection result');
      setGenerating(false);
      setPendingGeneration(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Theme V2 generation');
      setGenerating(false);
      setPendingGeneration(null);
    }
  }, [applyGenerationResponse, canGenerate, liveAnalysis, model, persistAnalysis, projectId, sourceImageUrls, themeName]);

  React.useEffect(() => {
    if (!taskError) return;
    setError(taskError.message);
    setGenerating(false);
    setActiveTaskId(null);
    setPendingGeneration(null);
  }, [taskError]);

  React.useEffect(() => {
    if (!generationTask || !pendingGeneration) return;

    if (generationTask.status === 'completed') {
      const response = generationTask.result as unknown as AgentGenerateThemeV2FromImageResponse | undefined;
      if (!response?.projectionPatch) {
        setError('Theme V2 generation completed without a projection result');
        setGenerating(false);
        setActiveTaskId(null);
        setPendingGeneration(null);
        return;
      }
      applyGenerationResponse(response, pendingGeneration);
      return;
    }

    if (generationTask.status === 'error' || generationTask.status === 'cancelled') {
      setError(generationTask.error || 'Theme V2 generation task failed');
      setGenerating(false);
      setActiveTaskId(null);
      setPendingGeneration(null);
    }
  }, [applyGenerationResponse, generationTask, pendingGeneration]);

  const handleCreateTheme = React.useCallback(async () => {
    if (!draftTheme) return;
    setSaving(true);
    setError(null);
    try {
      await onCreateTheme(draftTheme);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create generated theme');
    } finally {
      setSaving(false);
    }
  }, [draftTheme, onClose, onCreateTheme]);

  const previewTheme = React.useMemo(
    () => (draftTheme?.flowDocument
      ? createEditableThemeV2FromDraft(draftTheme, {
        baseTheme,
        fallbackId: 'theme_v2_image_preview',
        fallbackName: 'Generated Theme',
      })
      : null),
    [baseTheme, draftTheme]
  );

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="lg"
      fullWidth
      data-testid="generate-theme-from-image-dialog"
      actions={draftTheme ? [
        {
          label: 'Regenerate',
          onClick: handleGenerate,
          disabled: generating || saving,
          loading: generating,
          startIcon: <RefreshIcon />,
          'data-testid': 'generate-theme-from-image-regenerate-btn',
        },
      ] : undefined}
      primaryAction={{
        label: draftTheme ? createActionLabel : 'Generate Draft',
        onClick: draftTheme ? handleCreateTheme : handleGenerate,
        disabled: draftTheme ? saving : !canGenerate,
        loading: draftTheme ? saving : generating,
        startIcon: <AutoAwesomeIcon />,
        'data-testid': draftTheme ? 'generate-theme-from-image-create-btn' : 'generate-theme-from-image-submit-btn',
      }}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" data-testid="generate-theme-from-image-error">
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <StyledTextField
            label="Theme Name"
            value={themeName}
            onChange={setThemeName}
            fullWidth
            disabled={generating || saving}
            data-testid="generate-theme-from-image-name-input"
          />
        </Stack>

        <Box data-testid="generate-theme-from-image-sources">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">
              Reference Images
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddSourceImage}
              disabled={generating || saving || sourceImages.length >= MAX_SOURCE_IMAGES}
              data-testid="generate-theme-from-image-add-source-btn"
            >
              Add Image
            </Button>
          </Stack>
          <Stack spacing={1.5}>
            {sourceImages.map((source, index) => {
              const firstInputProps = index === 0
                ? {
                    wrapperTestId: 'generate-theme-from-image-url-input',
                    inputTestId: 'generate-theme-from-image-url-input-field',
                    uploadButtonTestId: 'generate-theme-from-image-url-upload-btn',
                    previewTestId: 'generate-theme-from-image-url-preview',
                  }
                : {
                    wrapperTestId: `generate-theme-from-image-url-input-${index}`,
                    inputTestId: `generate-theme-from-image-url-input-${index}-field`,
                    uploadButtonTestId: `generate-theme-from-image-url-upload-btn-${index}`,
                    previewTestId: `generate-theme-from-image-url-preview-${index}`,
                  };

              return (
                <Stack
                  key={source.id}
                  direction="row"
                  spacing={1}
                  alignItems="flex-start"
                  data-testid={`generate-theme-from-image-source-row-${index}`}
                >
                  <Box sx={{ flex: 1 }} data-testid={firstInputProps.wrapperTestId}>
                    <MediaUploadField
                      value={source.value}
                      onChange={(value) => {
                        void handleSourceImageChange(index, value);
                      }}
                      mediaType="image"
                      label={index === 0 ? 'Image URL or upload' : `Image ${index + 1}`}
                      placeholder="Paste image URL or upload file"
                      helperText={source.uploading ? 'Uploading image...' : undefined}
                      disabled={generating || saving || source.uploading}
                      size="small"
                      showPreview
                      inputTestId={firstInputProps.inputTestId}
                      uploadButtonTestId={firstInputProps.uploadButtonTestId}
                      previewTestId={firstInputProps.previewTestId}
                    />
                  </Box>
                  <IconButton
                    aria-label={`Remove image ${index + 1}`}
                    onClick={() => handleRemoveSourceImage(index)}
                    disabled={generating || saving || source.uploading}
                    size="small"
                    sx={{ mt: 3.25 }}
                    data-testid={`generate-theme-from-image-remove-source-${index}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        <Box data-testid="generate-theme-from-image-model-selector">
          <Typography variant="subtitle2" gutterBottom>
            Model
          </Typography>
          <AvailableModelSelector
            projectId={projectId}
            value={model}
            onChange={setModel}
            label="Theme analysis model"
            modelUsage="vision"
            disabled={generating || saving}
          />
        </Box>

        {warnings.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {warnings.map((warning) => (
              <Chip key={warning} size="small" color="warning" label={warning} />
            ))}
          </Stack>
        )}

        {previewTheme && (
          <>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Draft Preview
              </Typography>
              <Box
                sx={{
                  height: 520,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <ThemeV2PreviewCarousel theme={previewTheme} compact projectId={projectId} />
              </Box>
            </Box>
          </>
        )}
      </Stack>
    </SDialog>
  );
};

export default GenerateThemeFromImageDialog;
